// The project presence connection: one socket per open project (`/v1/realtime/projects/{id}`). Fetches
// a fresh ticket for every connect, sends hello, pings every 25 s, reconnects with backoff, and keeps a
// roster of the other people (reduceRoster from @filmsquare/realtime). What we send is our whole
// ProjectPresence (the room replaces it on every send), throttled to ~15 per second so cursor moves
// coalesce. When the server has no presence (503 not configured, 404/501 no such route) the client says
// "unavailable" and retries only rarely, quietly: the studio works without it. Pure: socket, timers and
// the ticket call are injected.
import {
  emptyRoster,
  type PresenceRoster,
  type ProjectPresence,
  reduceRoster,
} from "@filmsquare/realtime/presence";
import { type SocketLike, socketUrl } from "../canvas/connection.ts";
import { backoffMs, same, shouldReconnect } from "../canvas/presence.ts";

export const PRESENCE_SEND_MS = 66; // ~15 per second
export const PRESENCE_PING_MS = 25_000;
/** How long to wait before asking again when the server has no presence at all. */
export const UNAVAILABLE_RETRY_MS = 120_000;
/** Close code the room sends when a socket outlived its ticket: reconnect at once. */
const EXPIRED = 4408;
const OPEN = 1;

export type PresenceStatus = "idle" | "connecting" | "live" | "offline" | "unavailable";

export interface PresenceClientState {
  status: PresenceStatus;
  roster: PresenceRoster;
}

export interface PresenceTimers {
  now: () => number;
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (h: unknown) => void;
  setInterval: (fn: () => void, ms: number) => unknown;
  clearInterval: (h: unknown) => void;
}

export interface PresenceClientOptions {
  getSession: () => Promise<{ ticket: string; path: string }>;
  createSocket: (url: string) => SocketLike;
  apiBase: string;
  pageOrigin: string;
  clientId: string;
  timers: PresenceTimers;
  rand?: () => number;
  sendIntervalMs?: number;
  onChange: (s: PresenceClientState) => void;
}

/** A ticket call that failed because the server has no presence (not a passing outage). */
export function isUnavailable(e: unknown): boolean {
  const s = (e as { status?: number } | null)?.status;
  return s === 503 || s === 404 || s === 405 || s === 501;
}

/** Drops keys whose value is undefined, so the wire payload matches what the room stores. */
function clean(p: ProjectPresence): ProjectPresence {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(p)) if (v !== undefined) out[k] = v;
  return out as ProjectPresence;
}

export class ProjectPresenceClient {
  private readonly o: PresenceClientOptions;
  private ws: SocketLike | null = null;
  private stopped = true;
  private epoch = 0;
  private attempt = 0;
  private retry: unknown = null;
  private ping: unknown = null;
  private sendTimer: unknown = null;
  private lastSentAt = Number.NEGATIVE_INFINITY;
  private lastSent: ProjectPresence | null = null;
  private mine: ProjectPresence = {};
  private st: PresenceClientState = { status: "idle", roster: emptyRoster() };

  constructor(options: PresenceClientOptions) {
    this.o = options;
  }

  get state(): PresenceClientState {
    return this.st;
  }

  /** Our own presence as last set (sent or about to be sent). */
  get presence(): ProjectPresence {
    return this.mine;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.epoch++;
    if (this.retry !== null) this.o.timers.clearTimeout(this.retry);
    this.retry = null;
    this.clearPing();
    this.cancelSend();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      ws.onmessage = null;
      try {
        ws.close(1000, "bye");
      } catch {
        // already closed
      }
    }
    this.set({ status: "idle", roster: emptyRoster() });
  }

  /**
   * Merge a change into our presence and send the whole of it: the first change goes out at once, the
   * ones after it within the interval coalesce into one trailing send. Identical states are not resent.
   */
  update(patch: ProjectPresence): void {
    this.mine = clean({ ...this.mine, ...patch });
    this.schedule();
  }

  private set(s: PresenceClientState): void {
    this.st = s;
    this.o.onChange(s);
  }

  private open(): boolean {
    return this.ws?.readyState === OPEN && this.st.status === "live";
  }

  private schedule(): void {
    if (!this.open() || this.sendTimer !== null) return;
    const wait = this.lastSentAt + (this.o.sendIntervalMs ?? PRESENCE_SEND_MS) - this.o.timers.now();
    if (wait <= 0) {
      this.flush();
      return;
    }
    this.sendTimer = this.o.timers.setTimeout(() => {
      this.sendTimer = null;
      this.flush();
    }, wait);
  }

  private flush(): void {
    if (!this.open()) return;
    if (this.lastSent && same(this.lastSent, this.mine)) return;
    this.lastSent = this.mine;
    this.lastSentAt = this.o.timers.now();
    this.ws!.send(JSON.stringify({ t: "presence", presence: this.mine }));
  }

  private cancelSend(): void {
    if (this.sendTimer !== null) this.o.timers.clearTimeout(this.sendTimer);
    this.sendTimer = null;
  }

  private async connect(): Promise<void> {
    const epoch = ++this.epoch;
    if (this.st.status !== "live") this.set({ ...this.st, status: "connecting" });
    let session: { ticket: string; path: string };
    try {
      session = await this.o.getSession();
    } catch (e) {
      if (epoch !== this.epoch || this.stopped) return;
      if (isUnavailable(e)) {
        this.set({ status: "unavailable", roster: emptyRoster() });
        return this.scheduleRetry(UNAVAILABLE_RETRY_MS);
      }
      this.set({ status: "offline", roster: emptyRoster() });
      return this.scheduleRetry();
    }
    if (epoch !== this.epoch || this.stopped) return;
    let ws: SocketLike;
    try {
      ws = this.o.createSocket(socketUrl(this.o.apiBase, session.path, session.ticket, this.o.pageOrigin));
    } catch {
      this.set({ status: "offline", roster: emptyRoster() });
      return this.scheduleRetry();
    }
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      ws.send(JSON.stringify({ t: "hello", clientId: this.o.clientId }));
      this.clearPing();
      this.ping = this.o.timers.setInterval(() => {
        if (this.ws?.readyState === OPEN) this.ws.send(JSON.stringify({ t: "ping" }));
      }, PRESENCE_PING_MS);
    };
    ws.onmessage = (ev) => {
      if (this.ws !== ws || typeof ev.data !== "string") return;
      let msg: Parameters<typeof reduceRoster>[1];
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (!msg || typeof msg !== "object") return;
      if (msg.t === "welcome") {
        this.attempt = 0;
        this.lastSent = null;
        this.lastSentAt = Number.NEGATIVE_INFINITY;
        this.set({ status: "live", roster: reduceRoster(this.st.roster, msg) });
        // A new connection knows nothing about us: send the whole presence again.
        this.flush();
        return;
      }
      const next = reduceRoster(this.st.roster, msg);
      if (next !== this.st.roster) this.set({ ...this.st, roster: next });
    };
    ws.onerror = () => {
      // the close event follows
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.clearPing();
      this.cancelSend();
      if (this.stopped) return;
      this.set({ status: "offline", roster: emptyRoster() });
      if (ev.code === EXPIRED) {
        this.attempt = 0;
        void this.connect();
      } else if (shouldReconnect(ev.code)) this.scheduleRetry();
    };
  }

  private scheduleRetry(ms?: number): void {
    if (this.stopped || this.retry !== null) return;
    const wait = ms ?? backoffMs(this.attempt++, (this.o.rand ?? Math.random)());
    this.retry = this.o.timers.setTimeout(() => {
      this.retry = null;
      if (!this.stopped) void this.connect();
    }, wait);
  }

  private clearPing(): void {
    if (this.ping !== null) this.o.timers.clearInterval(this.ping);
    this.ping = null;
  }
}
